/**
 * Frame math + segment helpers for sketch-object glyphs.
 *
 * A {@link SketchObjectFrame} is the local coordinate system of one placed
 * object: `center + u·dir + n·norm` with u/n in meters. Parametric glyph
 * builders draw with the helpers here; SVG glyphs are normalized to the unit
 * box `[-0.5, 0.5]²` and mapped through {@link transformUnitSegments}.
 *
 * All output is flat `[x1,y1,z, x2,y2,z, ...]` line-segment positions, the
 * same format as the door/window glyphs (see openingGlyphs.ts).
 */

import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import { pointAtArcLength } from '../../utils/skeleton/arcGeometry';
import type { Coordinate } from '@pointorama/potree-shared';
import { SketchObjectAnchorKind, type SketchObjectFrame } from '../types';
import { recordGlyphArc } from './glyphInstances';

/** Segments approximating a quarter-circle arc (door swings etc.). */
export const ARC_SEGMENTS = 12;

/** Lift of the glyph above the floor plane so it never z-fights the wall skin. */
export const SKETCH_OBJECT_Z_LIFT = 0.02;

/** Point at `center + u·dir + n·norm`. */
export function at(frame: SketchObjectFrame, u: number, n: number): Pt2 {
  return {
    x: frame.center.x + u * frame.dir.x + n * frame.norm.x,
    y: frame.center.y + u * frame.dir.y + n * frame.norm.y,
  };
}

/** Push a segment from a→b at the frame height into `out`. */
export function pushSegment(out: number[], a: Pt2, b: Pt2, z: number): void {
  out.push(a.x, a.y, z, b.x, b.y, z);
}

/** Push the outline of a rectangle spanning [u0,u1]×[n0,n1] in frame coords. */
export function pushRect(out: number[], frame: SketchObjectFrame, u0: number, u1: number, n0: number, n1: number): void {
  const z = frame.z;
  const a = at(frame, u0, n0);
  const b = at(frame, u1, n0);
  const c = at(frame, u1, n1);
  const d = at(frame, u0, n1);
  pushSegment(out, a, b, z);
  pushSegment(out, b, c, z);
  pushSegment(out, c, d, z);
  pushSegment(out, d, a, z);
}

/** Default dash pattern (m) of overhead/hidden element outlines (beam bounds). */
const DASH_LENGTH = 0.12;
const DASH_GAP = 0.08;

/**
 * Push a→b as a dashed line: n dashes with n−1 gaps, stretched so the segment
 * starts and ends on a dash. Dash sizes are world meters (they scale with
 * zoom like the rest of the glyph); very short segments degrade to one solid
 * dash.
 */
export function pushDashedSegment(out: number[], a: Pt2, b: Pt2, z: number, dashLen: number = DASH_LENGTH, gapLen: number = DASH_GAP): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return;
  const count = Math.max(1, Math.round((len + gapLen) / (dashLen + gapLen)));
  const actualDash = (len - (count - 1) * gapLen) / count;
  if (actualDash <= 0) {
    pushSegment(out, a, b, z);
    return;
  }
  const ux = dx / len;
  const uy = dy / len;
  for (let i = 0; i < count; i++) {
    const s = i * (actualDash + gapLen);
    out.push(a.x + ux * s, a.y + uy * s, z, a.x + ux * (s + actualDash), a.y + uy * (s + actualDash), z);
  }
}

/** Dashed variant of {@link pushRect} — the overhead-element (beam) outline. */
export function pushDashedRect(out: number[], frame: SketchObjectFrame, u0: number, u1: number, n0: number, n1: number, dashLen?: number, gapLen?: number): void {
  const z = frame.z;
  const a = at(frame, u0, n0);
  const b = at(frame, u1, n0);
  const c = at(frame, u1, n1);
  const d = at(frame, u0, n1);
  pushDashedSegment(out, a, b, z, dashLen, gapLen);
  pushDashedSegment(out, b, c, z, dashLen, gapLen);
  pushDashedSegment(out, c, d, z, dashLen, gapLen);
  pushDashedSegment(out, d, a, z, dashLen, gapLen);
}

/** Push a polyline through frame-coord points `[u, n][]`. */
export function pushPolyline(out: number[], frame: SketchObjectFrame, points: ReadonlyArray<readonly [number, number]>): void {
  for (let i = 0; i + 1 < points.length; i++) {
    pushSegment(out, at(frame, points[i][0], points[i][1]), at(frame, points[i + 1][0], points[i + 1][1]), frame.z);
  }
}

/**
 * Push an arc centred at frame coords (uc, nc) with `radius`, from angle `a0`
 * to `a1` (radians in the frame's u/n plane, 0 = +u, π/2 = +n).
 */
export function pushArc(out: number[], frame: SketchObjectFrame, uc: number, nc: number, radius: number, a0: number, a1: number, segments: number = ARC_SEGMENTS): void {
  const segStart = out.length;
  let prev: Pt2 | null = null;
  for (let i = 0; i <= segments; i++) {
    const t = a0 + ((a1 - a0) * i) / segments;
    const pt = at(frame, uc + radius * Math.cos(t), nc + radius * Math.sin(t));
    if (prev) pushSegment(out, prev, pt, frame.z);
    prev = pt;
  }
  recordGlyphArc(out, at(frame, uc, nc), radius, a1 - a0, segStart);
}

/**
 * Map unit-box glyph segments (flat `[ax,ay, bx,by, ...]`, coords in
 * `[-0.5, 0.5]²`, +y = frame `norm`) into world segments in `frame`,
 * appending to `out`.
 */
export function transformUnitSegments(unit: Float32Array, frame: SketchObjectFrame, out: number[]): void {
  for (let i = 0; i + 3 < unit.length; i += 4) {
    const a = at(frame, unit[i] * frame.width, unit[i + 1] * frame.depth);
    const b = at(frame, unit[i + 2] * frame.width, unit[i + 3] * frame.depth);
    pushSegment(out, a, b, frame.z);
  }
}

/** The frame's four footprint corners in world XY (for bounds/hit padding). */
export function frameCorners(frame: SketchObjectFrame): Pt2[] {
  const hw = frame.width / 2;
  const hd = frame.depth / 2;
  return [at(frame, -hw, -hd), at(frame, hw, -hd), at(frame, hw, hd), at(frame, -hw, hd)];
}

/**
 * Build the frame of a free-placed object from its persisted anchor.
 *  - POINT: anchor[0] = center, orientation from `rotation`.
 *  - LINE: anchor[0..1] = the run; center = midpoint, dir along the segment,
 *    width = segment length (overrides the width property).
 *  - POLYGON: center = centroid, orientation from `rotation` (the raw ring is
 *    the caller's to draw; the frame only carries the footprint box).
 * Returns null when the anchor is empty/degenerate.
 */
export function buildFrameFromAnchor(kind: SketchObjectAnchorKind, anchor: Coordinate[], rotation: number, width: number, depth: number, z: number): SketchObjectFrame | null {
  if (anchor.length === 0) return null;
  const dirFromRotation: Pt2 = { x: Math.cos(rotation), y: Math.sin(rotation) };
  const normFromRotation: Pt2 = { x: -dirFromRotation.y, y: dirFromRotation.x };

  switch (kind) {
    case SketchObjectAnchorKind.POINT: {
      return { center: { x: anchor[0].x, y: anchor[0].y }, dir: dirFromRotation, norm: normFromRotation, width, depth, z };
    }
    case SketchObjectAnchorKind.LINE: {
      if (anchor.length < 2) return null;
      const dx = anchor[1].x - anchor[0].x;
      const dy = anchor[1].y - anchor[0].y;
      const len = Math.hypot(dx, dy);
      if (len <= 1e-6) return null;
      const dir: Pt2 = { x: dx / len, y: dy / len };
      return {
        center: { x: (anchor[0].x + anchor[1].x) / 2, y: (anchor[0].y + anchor[1].y) / 2 },
        dir,
        norm: { x: -dir.y, y: dir.x },
        width: len,
        depth,
        z,
      };
    }
    case SketchObjectAnchorKind.POLYGON: {
      let cx = 0;
      let cy = 0;
      for (const p of anchor) {
        cx += p.x;
        cy += p.y;
      }
      cx /= anchor.length;
      cy /= anchor.length;
      return { center: { x: cx, y: cy }, dir: dirFromRotation, norm: normFromRotation, width, depth, z };
    }
  }
}

/**
 * Build the frame of a wall-attached object from its live wall edge.
 *
 * @param start/end   wall edge centerline endpoints (world XY)
 * @param offset      distance along the edge from `start` to the object's near end (m)
 * @param side        true → the object sits on the +90°-CCW side of start→end
 * @param faceDistance distance from the edge centerline to the wall FACE on the
 *                     chosen side (the edge's left/right thickness)
 * @param inset       gap between the wall face and the object's back (m)
 *
 * The resulting frame's `norm` points away from the wall, so the object back
 * (local −depth/2) rests at `faceDistance + inset` off the centerline.
 */
/**
 * Frame resting on an explicit face LINE — used in the Measured view, where
 * the measured wall face need not be parallel to the sketch edge: the object
 * orients to the face (rotation follows its direction) and its back sits
 * flush on it. `anchor` is the object's along-position on the sketch
 * centerline; it is projected onto the face line so sliding along the wall
 * still feels like sliding along the edge.
 */
export function buildFrameFromWallFaceLine(anchor: Pt2, face: { point: Pt2; dir: Pt2 }, side: boolean, inset: number, width: number, depth: number, z: number): SketchObjectFrame {
  const sideSign = side ? 1 : -1;
  const norm: Pt2 = { x: -face.dir.y * sideSign, y: face.dir.x * sideSign };
  const t = (anchor.x - face.point.x) * face.dir.x + (anchor.y - face.point.y) * face.dir.y;
  const foot: Pt2 = { x: face.point.x + face.dir.x * t, y: face.point.y + face.dir.y * t };
  const perpCenter = inset + depth / 2;
  return {
    center: { x: foot.x + norm.x * perpCenter, y: foot.y + norm.y * perpCenter },
    dir: face.dir,
    norm,
    width,
    depth,
    z,
  };
}

export function buildFrameFromWall(start: Pt2, end: Pt2, offset: number, side: boolean, faceDistance: number, inset: number, width: number, depth: number, z: number, bulge = 0): SketchObjectFrame | null {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (Math.hypot(dx, dy) <= 1e-6) return null;
  // Along-edge coordinates are arc lengths on curved walls; the frame sits at
  // the object's center and orients to the LOCAL tangent there. The object's
  // rigid rectangle is a documented approximation on tight curves — its back
  // touches the wall face at the object's center.
  const alongCenter = offset + width / 2;
  const { point, tangent } = pointAtArcLength(start, end, bulge, alongCenter);
  const dir: Pt2 = tangent;
  const sideSign = side ? 1 : -1;
  const norm: Pt2 = { x: -dir.y * sideSign, y: dir.x * sideSign };
  const perpCenter = faceDistance + inset + depth / 2;
  return {
    center: {
      x: point.x + norm.x * perpCenter,
      y: point.y + norm.y * perpCenter,
    },
    dir,
    norm,
    width,
    depth,
    z,
  };
}
